import React, { useState, useEffect } from 'react';
import { Card, Button } from 'antd';
import { UpCircleOutlined, DownCircleOutlined } from '@ant-design/icons';
import { LinkData } from '../../api/api';
import { format, parseISO } from 'date-fns';

interface VideoCardProps {
  item: LinkData;
  onClickVideoUp: (item: LinkData) => void;
  onClickVideoDown: (item: LinkData) => void;
  onClickVideoDelete: (link: string) => void;
  userLink: (user: any) => React.JSX.Element;
  isEditing: boolean;
}

const VideoCard = ({ item, onClickVideoUp, onClickVideoDown, onClickVideoDelete, userLink, isEditing }: VideoCardProps) => {
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [clipId, setClipId] = useState('');

  useEffect(() => {
    const youtubeClipRegex = /^https?:\/\/(www\.)?youtube\.com\/clip\/([a-zA-Z0-9_-]+)/;
    if (!item?.link) return;

    const clipMatch = item.link.match(youtubeClipRegex);
    if (clipMatch) {
      // Just use the unique clip ID
      setClipId(clipMatch[2]);
      setThumbnailUrl('');
    } else {
      // Standard YouTube video
      setThumbnailUrl(`https://img.youtube.com/vi/${item.link}/hqdefault.jpg`);
      setClipId('');
    }
  }, [item?.link]);

  const handleClick = () => {
    try {
      if (clipId) {
        window.open(item.link, '_blank', 'noopener,noreferrer');
      } else {
        const embedUrl = `https://www.youtube.com/embed/${item.link}`;
        window.open(embedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to open URL:', err);
    }
  };

  return (
    <div onClick={handleClick} style={{ cursor: 'pointer' }}>
      <Card
        hoverable
        style={{ height: '100%' }}
        cover={
          <div
            style={{
              height: '180px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: clipId ? '#000' : undefined,
              color: clipId ? '#fff' : undefined,
              textAlign: 'center',
              padding: clipId ? '0 8px' : undefined,
            }}
          >
            {clipId ? (
              <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{clipId}</span>
            ) : thumbnailUrl ? (
              <img
                alt="YouTube Thumbnail"
                src={thumbnailUrl}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : null}
          </div>
        }
        actions={[
          <UpCircleOutlined key="up" onClick={(e) => { e.stopPropagation(); onClickVideoUp(item); }} />,
          <span key="votes">{item?.votes ?? 0}</span>,
          <DownCircleOutlined key="down" onClick={(e) => { e.stopPropagation(); onClickVideoDown(item); }} />,
          isEditing ? (
            <Button
              key="delete"
              danger
              size="small"
              onClick={(e) => { e.stopPropagation(); onClickVideoDelete(item.link!); }}
            >
              Delete
            </Button>
          ) : null,
        ].filter(Boolean)}
      >
        <Card.Meta
          title={<>Added by: {userLink(item?.addedBy || { id: 'error', username: 'error' })}</>}
          description={item?.dateAdded && format(parseISO(item.dateAdded), 'MM/dd/yyyy')}
        />
      </Card>
    </div>
  );
};

export default VideoCard;
